import styled from '@emotion/styled';
import AppContainer from '@layout/Components/AppContainer';
import { mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import { getQuizDetails, getIsPendingAction } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getIsQuizProctored } from '@cont/Quiz/utils';
import SimpleHeader from '@layout/Header/SimpleHeader';
import HeaderUserOptions from '@layout/Header/HeaderUserOptions';
import { useState, useEffect, useCallback, useRef } from 'react';
import Button from '@base/Button/Button';
import useWindowSize from '@hooks/useWindowSize';
import Paper from '@base/Paper/Paper';
import P2 from '@base/P2/P2';
import Checkbox from '@base/Checkbox/Checkbox';
import P1 from '@base/P1/P1';
import Divider from '@base/Divider/Divider';
import pushRoute from '@utils/pushRoute';
import { ProctorPermissionDialog } from '@cont/Quiz/Proctoring';
import useProctorSessionFlow from '../../Common/useProctorSessionFlow';
import Header from './Header';
import InstructionsInfo from './InstructionsInfo/InstructionsInfo';
import { INSTRUCTIONS_PAGE } from './constants';

const QuizLandingContainer = styled(AppContainer)`
  margin-top: ${Spacings.SPACING_10B};
  grid-auto-rows: max-content;

  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_4B};
    padding: 0 ${Spacings.SPACING_2B};
    width: 100%;
    display: flex;
    flex-flow: column;
  }
`;

const Wrapper = styled.div`
  grid-column: span 24;
  padding-bottom: ${Spacings.SPACING_20B};
`;

const InstructionFooter = styled(Paper)`
  position: fixed;
  bottom: 0;
  height: ${Spacings.SPACING_20B};
  grid-column: span 24;
  width: ${({ $width }) => $width || 0};
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_8B};
  display: flex;
  justify-content: end;
`;

const Footer = ({ handleOnEnroll, footerWidth, isEnrolling }) => {
  const isPendingAction = useSelectorWithShallowEqual(getIsPendingAction);

  return (
    <InstructionFooter $width={footerWidth}>
      <Button
        grid={4}
        variant={isPendingAction || isEnrolling ? 'disabled' : 'filled'}
        label={isEnrolling ? 'Initializing...' : 'Proceed'}
        onClick={handleOnEnroll}
        loading={isEnrolling}
      />
    </InstructionFooter>
  );
};

const CheckWrapper = styled(Checkbox)`
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_16B};

  .MuiIconButton-label {
    margin-bottom: 30px;
  }

  .MuiButtonBase-root {
    color: ${({ $checkboxColor }) => $checkboxColor};
  }
`;

const StyledP1Blue = styled(P1)`
  color: var(--color-i-blue-23);
  margin: ${Spacings.SPACING_6B} 0;
`;

const InstructionsPageContent = ({ isProctored }) => {
  const { query, pathname } = useRouter();
  const { quizUID, quizSlug } = query;
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const [isChecked, setIsChecked] = useState(false);
  const [showErrorOnCheckbox, setShowErrorOnCheckbox] = useState(false);

  const headerRef = useRef(null);
  const size = useWindowSize();
  const [footerWidth, setFooterWidth] = useState('auto');

  useEffect(() => {
    if (headerRef.current?.clientWidth) {
      setFooterWidth(`${headerRef.current.clientWidth}px`);
    }
  }, [size]);

  // Shallow push back to the same page. This keeps Layout/ProctorProvider
  // mounted so the SDK session survives - pushing to a separate route would
  // remount Layout and force the taking page to re-request permissions.
  const navigateToTaking = useCallback(
    () =>
      pushRoute({ pathname, query: { quizUID, quizSlug } }, undefined, {
        shallow: true
      }),
    [pathname, quizUID, quizSlug]
  );

  const {
    isEnrolling,
    showPermissionDialog,
    screenShareDenied,
    handleProceedClick,
    handlePermissionsGranted,
    handlePermissionsCancelled
  } = useProctorSessionFlow({ isProctored, onEnrolled: navigateToTaking });

  const showSectionInfo = quizDetails?.sections?.length > 1;

  const handleCheckboxClick = () => {
    setShowErrorOnCheckbox(false);
    setIsChecked((prev) => !prev);
  };

  const handleOnEnroll = async () => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth'
    });

    if (isChecked) {
      setShowErrorOnCheckbox(false);
      const succeeded = await handleProceedClick();
      if (succeeded) return;
    }
    return setShowErrorOnCheckbox(true);
  };

  const getCheckboxColor = () => {
    if (showErrorOnCheckbox) {
      return 'var(--color-i-red)';
    }
    if (isChecked) {
      return 'var(--color-text-primary)';
    }
    return 'var(--color-divider)';
  };

  return (
    <>
      <SimpleHeader>
        <HeaderUserOptions />
      </SimpleHeader>
      <QuizLandingContainer>
        <Wrapper>
          <Header ref={headerRef} />
          <InstructionsInfo
            name={quizDetails?.title}
            time={quizDetails?.duration}
            showSectionInfo={showSectionInfo}
          />
          <Divider />
          <StyledP1Blue>{INSTRUCTIONS_PAGE.NOTE_TEXT}</StyledP1Blue>
          <Divider />
          <CheckWrapper
            $checkboxColor={getCheckboxColor()}
            checked={isChecked}
            onChange={handleCheckboxClick}
            label={
              <P2
                color={
                  showErrorOnCheckbox
                    ? 'var(--color-i-red)'
                    : 'var(--color-text-primary)'
                }
              >
                {INSTRUCTIONS_PAGE.TNC_TEXT}
              </P2>
            }
          />
        </Wrapper>
        <Footer
          handleOnEnroll={handleOnEnroll}
          footerWidth={footerWidth}
          isEnrolling={isEnrolling}
        />
      </QuizLandingContainer>

      {/* Permission Dialog - acquires streams and stores them in ProctorProvider */}
      {showPermissionDialog && (
        <ProctorPermissionDialog
          onComplete={handlePermissionsGranted}
          onCancel={handlePermissionsCancelled}
          initialScreenPermissionState={screenShareDenied ? 'denied' : 'prompt'}
        />
      )}
    </>
  );
};

// Layout.jsx already wraps the whole quiz flow (instructions + taking) in a
// single ProctorProvider so the SDK/session survives that transition. Adding
// a second provider here would shadow it with independent state that gets
// torn down the moment this component unmounts, forcing the taking page to
// re-request permissions from scratch.
const InstructionsPage = () => {
  const { query } = useRouter();
  const { quizUID } = query;
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const { isProctored } = getIsQuizProctored(quizDetails);
  return <InstructionsPageContent isProctored={isProctored} />;
};

export default InstructionsPage;
